import { isDefined, isNil } from "@clipboard-health/util-ts";
import { CardContent, Link, Stack, type SxProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { mergeSxProps } from "../../lib/mergeSxProps";
import { Button, type ButtonProps } from "../Button";
import { Card } from "../Card";
import { CbhIcon, type IconType } from "../CbhIcon";
import { LoadingSpinner } from "../LoadingSpinner";
import { Text } from "../Text";

type BorderVariant = "solid" | "gradient" | "none";

interface PromotionBannerProps {
  title: string;
  subtitle?: string;
  icon: IconType;
  onClick?: () => void;
  backgroundColor?: (theme: Theme) => string;
  titleColor?: (theme: Theme) => string;
  borderColor?: (theme: Theme) => string;
  borderVariant: BorderVariant;
  /** Default: `contained` */
  ctaButtonVariant?: ButtonProps["variant"];
  /** If passed, no chevron-right icon will be rendered */
  ctaButtonText?: string;
  ctaButtonIsLoading?: boolean;
  sx?: SxProps<Theme>;
}

export function PromotionBanner(props: PromotionBannerProps) {
  const {
    title,
    subtitle,
    icon,
    onClick,
    backgroundColor,
    titleColor,
    borderColor,
    borderVariant,
    ctaButtonVariant = "contained",
    ctaButtonText,
    ctaButtonIsLoading = false,
    sx,
    ...restProps
  } = props;

  const getBackground = (theme: Theme) => {
    const bgColorValue = isDefined(backgroundColor)
      ? backgroundColor(theme)
      : theme.palette.info.light;

    if (borderVariant === "gradient") {
      // combine backgroundColor and border colors into single backgroundColor
      // for gradient effect
      const borderColorValue = isDefined(borderColor)
        ? borderColor(theme)
        : theme.palette.info.light;
      const borderColorWithoutSemicolon = borderColorValue?.replace(/;$/, "");

      return `
        linear-gradient(45deg, ${bgColorValue}, ${bgColorValue}) padding-box,
        ${borderColorWithoutSemicolon} border-box
      `;
    }

    return bgColorValue;
  };

  const getBorder = (theme: Theme) => {
    const borderSize = theme.borderWidth.xThick;

    if (borderVariant === "gradient") {
      return `${borderSize} solid transparent`;
    }

    if (borderVariant === "none" || !isDefined(borderColor)) {
      return "none";
    }

    return `${borderSize} solid ${borderColor(theme)}`;
  };

  return (
    <Card
      sx={mergeSxProps(
        {
          borderRadius: (theme) => theme.borderRadius!.medium,
          display: "flex",
          alignItems: "center",
          background: getBackground,
          border: getBorder,
        },
        sx
      )}
      onClick={onClick}
      {...restProps}
    >
      <CardContent
        sx={{
          width: "100%",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={4} alignItems="center">
            <CbhIcon type={icon} size="xLarge" />
            <Stack direction="column" spacing={3}>
              <Text semibold variant="body1" sx={(theme) => ({ color: titleColor?.(theme) })}>
                {title}
              </Text>
              {isDefined(subtitle) && <Text variant="body2">{subtitle}</Text>}
            </Stack>
          </Stack>
          {isNil(ctaButtonText) && isDefined(onClick) ? <CbhIcon type="chevron-right" /> : null}
        </Stack>
        {isDefined(ctaButtonText) ? (
          <Button
            fullWidth
            LinkComponent={Link}
            variant={ctaButtonVariant}
            disabled={ctaButtonIsLoading}
            sx={(theme) => ({ marginTop: theme.spacing(5) })}
            onClick={
              onClick
                ? (event) => {
                    // prevent parent Card from also handling the click, leading to double-handling
                    event.stopPropagation();
                    onClick();
                  }
                : undefined
            }
          >
            {ctaButtonIsLoading ? <LoadingSpinner size="small" /> : ctaButtonText}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
